/**
 * Strategies slice — the shared, READ-ONLY strategy signals (Python strategy
 * bots on one strategy MT5 account, see features/strategy_signals.md).
 *
 * Separate from `signal` (the old single bot_all view, which stays as-is) and
 * from `trading` (the user's own account). Sourced from `/api/strategies/*`:
 * a one-shot list on sign-in, then the `/api/strategies/stream` SSE, whose
 * snapshots carry every strategy's live status plus the order book (open
 * positions + pending orders) of the strategies this user has selected.
 *
 * The user picks any number of strategies; their orders are drawn together on
 * the Strategies chart, each in its strategy's colour (only the charted
 * instrument's levels draw, and a flat strategy draws nothing). The selection
 * and the chart symbol are remembered per device (the Strategies view is not a
 * real tab, so it has no workspace slot — same as the signal chart).
 */
import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { api } from "@/api/client";
import type {
  StrategiesResponse,
  StrategyHistoryResponse,
  StrategyRunnerInfo,
  StrategySummary,
  StrategyTrade,
  TradingOrder,
  TradingPosition,
} from "@/api/types";

/** Reserved tab id for the Strategies chart's Chart instance (never a real tab). */
export const STRATEGIES_TAB_ID = "__strategies__";
export const STRATEGY_SYMBOLS = ["XAUUSD", "BTCUSD"] as const;

const LS_SELECTED = "tickview.strategies.selected";
const LS_SYMBOL = "tickview.strategies.symbol";

function readLs(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLs(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode / blocked storage: selection just isn't remembered */
  }
}

function initialSelected(): string[] {
  const raw = readLs(LS_SELECTED);
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function initialSymbol(): string {
  const raw = readLs(LS_SYMBOL);
  return raw && (STRATEGY_SYMBOLS as readonly string[]).includes(raw) ? raw : "XAUUSD";
}

export interface StrategyBook {
  positions: TradingPosition[];
  orders: TradingOrder[];
}

interface StrategiesState {
  enabled: boolean;
  online: boolean;
  runner: StrategyRunnerInfo;
  strategies: StrategySummary[];
  book: Record<string, StrategyBook>;
  version: number | null;
  streamConnected: boolean;
  selectedIds: string[];
  /** Symbol the Strategies chart shows (XAUUSD / BTCUSD). */
  symbol: string;
  viewOpen: boolean;
  pickerOpen: boolean;
  /** Focus mode: while set, only this strategy's orders are drawn/listed. */
  focusedId: string | null;
  /** Right-rail docked drawer: the selected strategies' positions + orders. */
  ordersOpen: boolean;
  history: Record<string, StrategyTrade[]>;
  listStatus: "idle" | "loading" | "succeeded" | "failed";
}

const initialState: StrategiesState = {
  enabled: false,
  online: false,
  runner: { state: "offline" },
  strategies: [],
  book: {},
  version: null,
  streamConnected: false,
  selectedIds: initialSelected(),
  symbol: initialSymbol(),
  viewOpen: false,
  pickerOpen: false,
  focusedId: null,
  ordersOpen: false,
  history: {},
  listStatus: "idle",
};

export const fetchStrategies = createAsyncThunk<StrategiesResponse>(
  "strategies/list",
  async () => api.strategies(),
);

export const fetchStrategyHistory = createAsyncThunk<StrategyHistoryResponse, string>(
  "strategies/history",
  async (id) => api.strategyHistory(id),
);

function applyDoc(state: StrategiesState, doc: StrategiesResponse): void {
  state.enabled = doc.enabled;
  state.online = doc.online;
  state.runner = doc.runner ?? { state: "offline" };
  state.strategies = doc.strategies ?? [];
  if (doc.book) state.book = doc.book;
  if (doc.version != null) state.version = doc.version;
  // Drop selections for strategies that are no longer visible to this user.
  const known = new Set(state.strategies.map((s) => s.id));
  if (state.strategies.length > 0) {
    const kept = state.selectedIds.filter((id) => known.has(id));
    if (kept.length !== state.selectedIds.length) {
      state.selectedIds = kept;
      writeLs(LS_SELECTED, JSON.stringify(kept));
    }
    if (state.focusedId && !known.has(state.focusedId)) state.focusedId = null;
  }
}

const strategiesSlice = createSlice({
  name: "strategies",
  initialState,
  reducers: {
    applyStrategiesSnapshot: (state, action: PayloadAction<StrategiesResponse>) => {
      applyDoc(state, action.payload);
    },
    setStrategiesStreamConnected: (state, action: PayloadAction<boolean>) => {
      state.streamConnected = action.payload;
    },
    /** Replace the selection (the picker's "Show on chart"). */
    setSelectedStrategies: (state, action: PayloadAction<string[]>) => {
      const unique = Array.from(new Set(action.payload));
      state.selectedIds = unique;
      writeLs(LS_SELECTED, JSON.stringify(unique));
    },
    removeSelectedStrategy: (state, action: PayloadAction<string>) => {
      state.selectedIds = state.selectedIds.filter((id) => id !== action.payload);
      if (state.focusedId === action.payload) state.focusedId = null;
      writeLs(LS_SELECTED, JSON.stringify(state.selectedIds));
    },
    setStrategiesSymbol: (state, action: PayloadAction<string>) => {
      if (!(STRATEGY_SYMBOLS as readonly string[]).includes(action.payload)) return;
      state.symbol = action.payload;
      writeLs(LS_SYMBOL, action.payload);
    },
    setStrategiesViewOpen: (state, action: PayloadAction<boolean>) => {
      const opening = action.payload && !state.viewOpen;
      state.viewOpen = action.payload;
      // The picker NEVER opens itself. It used to, on a first visit with an
      // empty selection, which threw a modal over the guided tour explaining
      // the very button that opens it. The bar already prompts ("Pick
      // strategies to see their orders on the chart") and the tour points at
      // the button — the user opens it when they want it.
      //
      // The orders drawer still comes up with the view, the way the Signal tab
      // opens its own, but only when there is something to put in it.
      if (opening && state.selectedIds.length > 0) state.ordersOpen = true;
      if (!action.payload) {
        // Leaving the view takes its surfaces with it — the same rule the
        // Signal view follows in setSignalChartOpen. The drawer lists the
        // STRATEGY account's orders, so left open over a normal chart it reads
        // as that chart's own orders, and it steals width from the chart the
        // user just switched to. Covers every exit: picking another tab, the
        // pill's ×, or switching to the Signal view.
        state.pickerOpen = false;
        state.ordersOpen = false;
      }
    },
    setStrategyPickerOpen: (state, action: PayloadAction<boolean>) => {
      state.pickerOpen = action.payload;
    },
    /** Isolate one selected strategy on the chart (null clears focus). */
    setFocusedStrategy: (state, action: PayloadAction<string | null>) => {
      state.focusedId = action.payload;
    },
    setStrategyOrdersOpen: (state, action: PayloadAction<boolean>) => {
      state.ordersOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStrategies.pending, (state) => {
        state.listStatus = "loading";
      })
      .addCase(fetchStrategies.fulfilled, (state, action) => {
        state.listStatus = "succeeded";
        applyDoc(state, action.payload);
      })
      .addCase(fetchStrategies.rejected, (state) => {
        state.listStatus = "failed";
        state.enabled = false;
      })
      .addCase(fetchStrategyHistory.fulfilled, (state, action) => {
        state.history[action.payload.strategy] = action.payload.trades;
      });
  },
});

export const {
  applyStrategiesSnapshot,
  setStrategiesStreamConnected,
  setSelectedStrategies,
  removeSelectedStrategy,
  setStrategiesSymbol,
  setStrategiesViewOpen,
  setStrategyPickerOpen,
  setStrategyOrdersOpen,
  setFocusedStrategy,
} = strategiesSlice.actions;

export default strategiesSlice.reducer;
